import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import api, { OPERATING_GROUP_CHANGED_EVENT, OPERATING_GROUP_STORAGE_KEY } from '../../api/api';
import { RestaurantAttendance, RestaurantEmployment, RestaurantEmploymentListItem, RestaurantHrContext,
  RestaurantPage, RestaurantShift } from '../../types/restaurantPersonnel';
import RestaurantAttendanceHistory from '../../components/RestaurantAttendanceHistory';
import RestaurantEmployeePrintControl from '../../components/RestaurantEmployeePrintControl';
import RestaurantAttendanceEditor, { preserveCivilTime } from '../../components/RestaurantAttendanceEditor';
import RestaurantAttendanceGrid from '../../components/RestaurantAttendanceGrid';
import { extractApiErrorMessage } from '../../utils/apiError';
import { createIdempotencyKey } from '../../utils/operationKey';
import { formatCivilDate, formatCivilDateTime } from '../../utils/restaurantPersonnelFormat';
import RestaurantDossierPanel from './RestaurantDossierPanel';
import RestaurantAccountPicker from './RestaurantAccountPicker';
import './RestaurantPersonnel.css';

type Access = { permissions: string[]; editPermissions: string[] };
type Department = { departmentId: number; name: string };
type Score = { id?: number; blockType: string; name: string; description: string; score: number; itemType: string };
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const freshEmployee = (group: number): RestaurantEmployment => ({
  id: 0, operatingGroupId: group, appUserId: '', effectiveFrom: today(), effectiveTo: null,
  fullName: '', position: '', departmentId: null, phone: '', hireDate: null, baseRate: 0,
  useAttendance: true, useFixedRateSalary: false, fixedMonthlyRate: 0, revenuePercent: 0,
  monthsWorked: 0, positionCoefficient: 1, correction: 0, useFixedCoefficient: false,
  fixedCoefficient: 0.32, comment: '', version: 0, managePersonnel: false, manageSchedule: false,
  viewDossiers: false, editDossiers: false,
});
const timeLabel = (value: string | null) => value ? formatCivilDateTime(value) : 'Відкрито';
const inputTime = (value: string | null) => value?.slice(0, 16) ?? '';
export { preserveCivilTime } from '../../components/RestaurantAttendanceEditor';

const RestaurantPersonnelWorkspace: React.FC = () => {
  const [context, setContext] = useState<RestaurantHrContext | null>(null);
  const [access, setAccess] = useState<Access>({ permissions: [], editPermissions: [] });
  const [employees, setEmployees] = useState<RestaurantEmploymentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [listReady, setListReady] = useState(false);
  const [attendanceRefresh, setAttendanceRefresh] = useState(0);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employee, setEmployee] = useState<RestaurantEmployment | null>(null);
  const [originalEntry, setOriginalEntry] = useState<RestaurantAttendance | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [gridRefresh, setGridRefresh] = useState(0);
  const [view, setView] = useState<'personnel' | 'attendance'>('personnel');
  const [gridOpened, setGridOpened] = useState(false);
  const [shifts, setShifts] = useState<RestaurantShift[]>([]);
  const [scores, setScores] = useState<Score[]>([]);
  const [query, setQuery] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [operationBusy, setBusy] = useState(false);
  const [accountBusy, setAccountBusy] = useState(false);
  const busy = operationBusy || accountBusy;
  const [shift, setShift] = useState({ id: 0, startTime: '', endTime: '', isCompleted: false, notes: '', version: 0 });
  const detailGeneration = useRef(0);
  const cardHeading = useRef<HTMLHeadingElement>(null);
  const focusCardAfterSelection = useRef(false);
  const nextDetailGeneration = useCallback(() => ++detailGeneration.current, []);
  const nextScoreId = useRef(-1);
  const pending = useRef(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const employmentKey = useRef(createIdempotencyKey());
  const departmentKey = useRef(createIdempotencyKey());
  const edit = !!context?.managePersonnel && access.editPermissions.includes('page.admin');
  const personnelView = !!context?.managePersonnel && access.permissions.includes('page.admin');
  const scheduleView = !!context?.manageSchedule && access.permissions.includes('page.management.schedule');
  const scheduleEdit = scheduleView && access.editPermissions.includes('page.management.schedule');
  const dossierView = !!context?.viewDossiers && access.permissions.includes('page.management.employee-dossiers');
  const dossierEdit = dossierView && !!context?.editDossiers && access.editPermissions.includes('page.management.employee-dossiers');

  useEffect(() => {
    if (!employee || !focusCardAfterSelection.current) return;
    focusCardAfterSelection.current = false;
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 900px)').matches) return;
    const heading = cardHeading.current;
    if (!heading) return;
    const frame = window.requestAnimationFrame(() => {
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [employee]);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!active.current) return;
    setListReady(false);
    setEmployees([]);
    const [scope, pageAccess] = await Promise.all([
      api.get<RestaurantHrContext>('/restaurant-personnel/context', { signal }),
      api.get<Access>('/users/me/access', { signal }),
    ]);
    if (signal?.aborted || !active.current) return;
    setContext(scope.data);
    setAccess(pageAccess.data);
    if (scope.data.managePersonnel && pageAccess.data.permissions.includes('page.admin')) {
      const [people, units] = await Promise.all([
        api.get<RestaurantPage<RestaurantEmploymentListItem>>('/restaurant-personnel',
          { signal, params: { page, pageSize: 25, search } }),
        api.get<Department[]>('/restaurant-personnel/departments', { signal }),
      ]);
      if (signal?.aborted || !active.current) return;
      setEmployees(people.data.items);
      setTotal(people.data.total);
      setDepartments(units.data);
      setListReady(true);
    } else {
      setEmployees([]);
      setDepartments([]);
    }
  }, [page, search]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal).catch(failure => {
      if (!controller.signal.aborted) {
        setContext(null);
        setEmployee(null);
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити персонал ресторану.'));
      }
    });
    return () => { controller.abort(); nextDetailGeneration(); };
  }, [load, nextDetailGeneration]);

  const select = async (selected: RestaurantEmploymentListItem | RestaurantEmployment, focusCard = false) => {
    if (!active.current) return;
    focusCardAfterSelection.current = focusCard;
    const generation = nextDetailGeneration();
    setEmployee('appUserId' in selected ? selected : null);
    setOriginalEntry(null);
    setShifts([]);
    setScores([]);
    setEditorKey(value => value + 1);
    setShift({ id: 0, startTime: '', endTime: '', isCompleted: false, notes: '', version: 0 });
    setError('');
    if (!selected.id) return;
    try {
      const [detail, evaluation, plan] = await Promise.all([
        api.get<RestaurantEmployment>(`/restaurant-personnel/${selected.id}`),
        api.get<Score[]>(`/restaurant-personnel/${selected.id}/scores`),
        scheduleView ? api.get<RestaurantShift[]>(`/restaurant-personnel/${selected.id}/shifts`) : Promise.resolve({ data: [] }),
      ]);
      if (generation !== detailGeneration.current) return;
      setEmployee(detail.data);
      setAttendanceRefresh(value => value + 1);
      setScores(evaluation.data);
      setShifts(plan.data);
    } catch (failure) {
      if (generation === detailGeneration.current) {
        focusCardAfterSelection.current = false;
        setEmployee(null);
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити картку.'));
      }
    }
  };

  const perform = async (action: () => Promise<void>) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setMessage('');
    setError('');
    try {
      await action();
      if (active.current) setMessage('Зміни збережено в обраному ресторані.');
    } catch (failure) {
      if (active.current) setError(extractApiErrorMessage(failure, 'Не вдалося підтвердити зміну. Оновіть дані перед повтором.'));
    } finally { pending.current = false; setBusy(false); }
  };

  const saveEmployee = (event: FormEvent) => {
    event.preventDefault();
    if (!employee || !employee.appUserId || busy) return;
    void perform(async () => {
      const response = employee.id
        ? await api.put<RestaurantEmployment>(`/restaurant-personnel/${employee.id}`, employee)
        : await api.post<RestaurantEmployment>('/restaurant-personnel', employee,
          { headers: { 'Idempotency-Key': employmentKey.current } });
      if (!active.current) return;
      employmentKey.current = createIdempotencyKey();
      setGridRefresh(value => value + 1);
      await load();
      await select(response.data);
    });
  };

  const set = <K extends keyof RestaurantEmployment>(key: K, value: RestaurantEmployment[K]) =>
    setEmployee(current => current ? { ...current, [key]: value } : current);
  const numberField = (key: 'monthsWorked' | 'positionCoefficient' | 'correction' | 'fixedCoefficient' |
    'fixedMonthlyRate' | 'revenuePercent' | 'baseRate', label: string, min: number | undefined, step: number | 'any') =>
    employee && <label key={key}>{label}<input type="number" required min={min} step={step}
      value={employee[key]} disabled={!edit || busy} onChange={event => set(key, Number(event.target.value))} /></label>;

  return <main className="page-shell restaurant-personnel">
    <h1>Персонал ресторану</h1>
    <details>
      <summary>Детальніше про картки персоналу</summary>
      <p>Картки, права, досьє, графік і табель належать лише обраному ресторану. Технічний вхід не є працевлаштуванням.</p>
    </details>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {!context && !error && <p role="status">Завантаження…</p>}
    {context && <>
      <p title={`ID ресторану: ${context.operatingGroupId}`}>
        Обраний ресторан{context.legacyHallsRestaurant ? ' · HALLS: історичний HR перенесено' : ''}
      </p>
      {context.systemAdministrator && !context.hasTransition &&
        <p><a href="?legacy=1">Історичний спільний HR (перехідний перегляд до підтвердженого перенесення)</a></p>}
      {!context.managePersonnel && <p>Ресторанний доступ до кадрового адміністрування не надано. Автоматичного успадкування з іншого ресторану немає.</p>}
      {personnelView && <div className="restaurant-personnel__actions" role="group" aria-label="Режим кадрового екрана">
        <button type="button" aria-pressed={view === 'personnel'} disabled={busy}
          onClick={() => setView('personnel')}>Працівники</button>
        <button type="button" aria-pressed={view === 'attendance'} disabled={busy} onClick={() => {
          setGridOpened(true); setView('attendance');
        }}>Місячний табель</button>
      </div>}
      {personnelView && gridOpened && <div hidden={view !== 'attendance'}>
        <RestaurantAttendanceGrid key={context.operatingGroupId} canEdit={edit}
          refreshKey={gridRefresh} onSaved={() => {
            setAttendanceRefresh(value => value + 1);
            void perform(load);
          }} />
      </div>}
      <div hidden={view !== 'personnel'}>
      {personnelView && <div className="restaurant-personnel__split">
        <aside>
          <h2>Працевлаштування</h2>
          <form onSubmit={event => { event.preventDefault(); setSearch(query.trim()); setPage(1); }}>
            <label>Пошук за ПІБ<input type="search" maxLength={200} value={query} onChange={event => setQuery(event.target.value)} /></label>
            <button type="submit" disabled={busy}>Шукати</button>
          </form>
          <div className="restaurant-personnel__actions">
            {edit && <button type="button" disabled={busy} onClick={() => {
              employmentKey.current = createIdempotencyKey();
              void select(freshEmployee(context.operatingGroupId));
            }}>Додати працевлаштування</button>}
            <button type="button" disabled={busy} onClick={() => void perform(load)}>Оновити список</button>
          </div>
          {listReady && total === 0 && <p>{search ? 'За пошуком працевлаштувань не знайдено.' :
            'У цьому ресторані персонал ще не заведено. Спільні HR-записи не підставляються.'}</p>}
          {listReady && <p role="status">Працевлаштувань: {total} · Сторінка {page}</p>}
          <div className="restaurant-personnel__list">
            {employees.map(person => <div key={person.id} className="restaurant-personnel__employee">
              <button type="button" aria-pressed={employee?.id === person.id}
                title={`ID працевлаштування: ${person.id}`}
                disabled={busy} onClick={() => void select(person, true)}>
                {person.fullName} · {person.position}<br />{formatCivilDate(person.effectiveFrom)} — {
                  person.effectiveTo ? formatCivilDate(person.effectiveTo) : 'працює'}
              </button>
              <p>{person.openAttendance ? `На зміні з ${formatCivilDateTime(person.openAttendance.checkInAt)}`
                : 'Відкритої зміни немає'}{!person.useAttendance ? ' · Табелювання вимкнено' : ''}
                {!person.activeToday ? ' · Не працює сьогодні' : ''}</p>
              {edit && <div className="restaurant-personnel__actions">
                <button type="button" aria-label={`Відмітити прихід: ${person.fullName}`}
                  disabled={busy || !listReady || !person.activeToday || !person.useAttendance || !!person.openAttendance}
                  onClick={() => void perform(async () => {
                    await api.post(`/restaurant-personnel/${person.id}/check-in`);
                    setGridRefresh(value => value + 1);
                    setAttendanceRefresh(value => value + 1);
                    await load();
                  })}>Прихід</button>
                <button type="button" aria-label={`Відмітити вихід: ${person.fullName}`}
                  disabled={busy || !listReady || !person.activeToday || !person.useAttendance || !person.openAttendance}
                  onClick={() => void perform(async () => {
                    if (!person.openAttendance) return;
                    await api.post(`/restaurant-personnel/${person.id}/check-out`, {
                      entryId: person.openAttendance.id, version: person.openAttendance.version,
                    });
                    setGridRefresh(value => value + 1);
                    setAttendanceRefresh(value => value + 1);
                    await load();
                  })}>Вихід</button>
              </div>}
            </div>)}
          </div>
          <div className="restaurant-personnel__actions">
            <button type="button" disabled={busy || !listReady || page === 1} onClick={() => setPage(page - 1)}>Попередня сторінка</button>
            <button type="button" disabled={busy || !listReady || page * 25 >= total} onClick={() => setPage(page + 1)}>Наступна сторінка</button>
          </div>
        </aside>
        <section>
          {!employee && <p>Оберіть картку або додайте працевлаштування.</p>}
          {employee && <>
            <h2 ref={cardHeading} tabIndex={-1} title={employee.id ? `ID працевлаштування: ${employee.id}` : undefined}>
              {employee.id ? 'Картка працівника' : 'Нове працевлаштування'}
            </h2>
            {!!employee.id && personnelView && <RestaurantEmployeePrintControl key={employee.id}
              employee={employee} disabled={busy}
              canViewSalary={access.permissions.includes('page.management.salary')} />}
            {!employee.id && edit && <RestaurantAccountPicker key={editorKey}
              systemAdministrator={context.systemAdministrator} disabled={busy}
              onBusyChange={setAccountBusy} onSelect={account => set('appUserId', account?.id ?? '')} />}
            <form onSubmit={saveEmployee}>
              <fieldset disabled={!edit || busy}>
                <legend>Локальний профіль і налаштування</legend>
                <div className="restaurant-personnel__form">
                  <label>ID технічного облікового запису<input readOnly value={employee.appUserId}
                    aria-describedby="employment-identity-help" /></label>
                  <label>ПІБ<input required maxLength={200} value={employee.fullName} onChange={event => set('fullName', event.target.value)} /></label>
                  <label>Посада<input required maxLength={200} value={employee.position} onChange={event => set('position', event.target.value)} /></label>
                  <div>
                    <label>Внутрішній підрозділ<select value={employee.departmentId ?? ''}
                      aria-describedby={departments.length === 0 ? 'employment-department-help' : undefined}
                      onChange={event => set('departmentId', event.target.value ? Number(event.target.value) : null)}>
                      <option value="">Без підрозділу</option>
                      {employee.departmentId != null && !departments.some(unit => unit.departmentId === employee.departmentId) &&
                        <option value={employee.departmentId} title={`ID підрозділу: ${employee.departmentId}`}>
                          Підрозділ (поза структурою ресторану)
                        </option>}
                      {departments.map(unit => <option key={unit.departmentId} value={unit.departmentId}>{unit.name}</option>)}
                    </select></label>
                    {departments.length === 0 && <small id="employment-department-help">
                      У ресторані ще немає підрозділів.{edit && ' Додайте їх унизу сторінки у розділі «Внутрішні підрозділи ресторану».'}
                    </small>}
                  </div>
                  <label>Працює з (включно)<input required type="date" value={employee.effectiveFrom} onChange={event => set('effectiveFrom', event.target.value)} /></label>
                  <label>Не працює з (виключно)<input type="date" value={employee.effectiveTo ?? ''} onChange={event => set('effectiveTo', event.target.value || null)} /></label>
                  <label>Телефон<input maxLength={60} value={employee.phone} onChange={event => set('phone', event.target.value)} /></label>
                  <label>Кадрова дата прийняття<input type="date" value={employee.hireDate?.slice(0, 10) ?? ''}
                    onChange={event => set('hireDate', event.target.value ? `${event.target.value}T00:00:00` : null)} /></label>
                  {numberField('monthsWorked', 'Стаж (місяців)', 0, 1)}
                  {numberField('baseRate', 'Базова ставка (₴)', 0, 'any')}
                  {numberField('positionCoefficient', 'Коефіцієнт посади', 0, 'any')}
                  {numberField('correction', 'Корекція коефіцієнта', undefined, 'any')}
                  {numberField('fixedCoefficient', 'Сталий коефіцієнт', 0, 'any')}
                  {numberField('fixedMonthlyRate', 'Місячна ставка (₴)', 0, 'any')}
                  {numberField('revenuePercent', 'Частка виторгу (%)', 0, 'any')}
                  <label><input type="checkbox" checked={employee.useAttendance} onChange={event => set('useAttendance', event.target.checked)} />Вести табель</label>
                  <label><input type="checkbox" checked={employee.useFixedCoefficient} onChange={event => set('useFixedCoefficient', event.target.checked)} />Використовувати сталий коефіцієнт</label>
                  <label><input type="checkbox" checked={employee.useFixedRateSalary} onChange={event => set('useFixedRateSalary', event.target.checked)} />Місячна ставка + частка виторгу</label>
                  <label>Примітка<textarea maxLength={2000} value={employee.comment} onChange={event => set('comment', event.target.value)} /></label>
                </div>
              </fieldset>
              <details>
                <summary>Детальніше про прив’язку облікового запису</summary>
                <p id="employment-identity-help">Крок 2. ПІБ та налаштування належать працевлаштуванню, а не логіну.
                  {!employee.id ? ' Спочатку явно оберіть або створіть обліковий запис вище.' : ' Прив’язаний обліковий запис незмінний.'}</p>
              </details>
              {edit && <button disabled={busy || !employee.appUserId} type="submit">Зберегти картку</button>}
            </form>
            <details>
              <summary>Детальніше про завершення працевлаштування</summary>
              <p>Закриття дати працевлаштування не видаляє технічний вхід, табель, досьє чи історію. Права в інших ресторанах не змінюються.</p>
            </details>
            {!!employee.id && <>
              {context.systemAdministrator && access.editPermissions.includes('page.admin') && <fieldset disabled={busy}>
                <legend>Явні права цього працевлаштування (системний адміністратор)</legend>
                <p role="note">Ці права зберігаються окремо від картки кнопкою «Зберегти права ресторану».</p>
                {(['managePersonnel', 'manageSchedule', 'viewDossiers', 'editDossiers'] as const).map((key, index) =>
                  <label key={key}><input type="checkbox" checked={employee[key]} onChange={event => set(key, event.target.checked)} />
                    {['Кадрове адміністрування', 'Керування графіком', 'Перегляд досьє', 'Зміни досьє'][index]}</label>)}
                <button type="button" onClick={() => void perform(async () => {
                  await api.put(`/restaurant-personnel/${employee.id}/rights`, {
                    managePersonnel: employee.managePersonnel, manageSchedule: employee.manageSchedule,
                    viewDossiers: employee.viewDossiers, editDossiers: employee.editDossiers, version: employee.version,
                  });
                  await load();
                  await select(employee);
                })}>Зберегти права ресторану</button>
              </fieldset>}
              <h3>Табель: окремі інтервали</h3>
              <RestaurantAttendanceHistory key={`attendance-${employee.id}`} path={`/restaurant-personnel/${employee.id}/attendance`}
                refreshKey={attendanceRefresh} disabled={busy}
                onEdit={edit ? item => {
                  setOriginalEntry(item);
                  setEditorKey(value => value + 1);
                } : undefined} />
              {edit && <RestaurantAttendanceEditor key={editorKey} employmentId={employee.id} original={originalEntry}
                disabled={busy || !employee.useAttendance} onNew={() => {
                  setOriginalEntry(null); setEditorKey(value => value + 1);
                }} onSaved={async () => {
                  setGridRefresh(value => value + 1);
                  setAttendanceRefresh(value => value + 1);
                  setOriginalEntry(null);
                  setEditorKey(value => value + 1);
                  setMessage('Інтервал збережено в обраному ресторані.');
                  try { await load(); }
                  catch (failure) {
                    setError(extractApiErrorMessage(failure, 'Інтервал збережено, але список не оновлено. Оновіть дані.'));
                  }
                }} onDeleted={async () => {
                  setGridRefresh(value => value + 1);
                  setAttendanceRefresh(value => value + 1);
                  setOriginalEntry(null);
                  setEditorKey(value => value + 1);
                  setMessage('Інтервал видалено з табелю ресторану.');
                  try { await load(); }
                  catch (failure) {
                    setError(extractApiErrorMessage(failure, 'Інтервал видалено, але список не оновлено. Оновіть дані.'));
                  }
                }} onSavingChange={saving => {
                  setBusy(saving);
                  if (saving) { setError(''); setMessage(''); }
                }} />}
              <h3>Оцінювання ресторану</h3>
              {scores.map((score, index) => <div className="restaurant-personnel__form" key={score.id}>
                <label>Блок<select disabled={!edit || busy} value={score.blockType} onChange={event => setScores(current =>
                  current.map((value, i) => i === index ? { ...value, blockType: event.target.value } : value))}>
                  <option value="qualification">Кваліфікація</option><option value="productivity">Продуктивність</option>
                  <option value="responsibility">Відповідальність</option>
                </select></label>
                <label>Назва оцінки<input required disabled={!edit || busy} value={score.name} onChange={event => setScores(current =>
                  current.map((value, i) => i === index ? { ...value, name: event.target.value } : value))} /></label>
                <label>Оцінка (0–10)<input type="number" min={0} max={10} step="any" required disabled={!edit || busy} value={score.score}
                  onChange={event => setScores(current => current.map((value, i) => i === index ? { ...value, score: Number(event.target.value) } : value))} /></label>
                <label>Тип<select disabled={!edit || busy} value={score.itemType} onChange={event => setScores(current =>
                  current.map((value, i) => i === index ? { ...value, itemType: event.target.value } : value))}>
                  <option value="positive">Додатна</option><option value="penalty">Штрафна</option>
                </select></label>
              </div>)}
              {edit && <div className="restaurant-personnel__actions">
                <button type="button" disabled={busy} onClick={() => setScores([...scores, {
                  id: nextScoreId.current--,
                  blockType: 'qualification', name: '', description: '', score: 0, itemType: 'positive',
                }])}>Додати оцінку</button>
                <button type="button" disabled={busy || scores.some(score => !score.name.trim())}
                  onClick={() => void perform(async () => {
                    await api.put(`/restaurant-personnel/${employee.id}/scores`, {
                      version: employee.version, scores: scores.map(score => ({ ...score, id: score.id && score.id > 0 ? score.id : null })),
                    }); await select(employee);
                  })}>Зберегти оцінювання</button>
              </div>}
              {scheduleView && <>
                <h3>Графік працівника</h3>
                {shifts.map(item => <p key={item.id}>
                  {timeLabel(item.startTime)} — {timeLabel(item.endTime)} · {item.isCompleted ? 'Виконано' : 'Заплановано'}
                  {scheduleEdit && <button type="button" disabled={busy} onClick={() => setShift({
                    id: item.id, startTime: inputTime(item.startTime), endTime: inputTime(item.endTime),
                    isCompleted: item.isCompleted, notes: item.notes, version: item.version,
                  })} title={`ID зміни: ${item.id}`}>Редагувати зміну</button>}
                </p>)}
                {scheduleEdit && <form onSubmit={event => { event.preventDefault(); void perform(async () => {
                  const original = shifts.find(value => value.id === shift.id);
                  await api.put(`/restaurant-personnel/${employee.id}/shifts/${shift.id}`, {
                    ...shift, startTime: preserveCivilTime(shift.startTime, original?.startTime),
                    endTime: preserveCivilTime(shift.endTime, original?.endTime),
                  }); await select(employee);
                }); }}>
                  <h4 title={shift.id ? `ID зміни: ${shift.id}` : undefined}>
                    {shift.id ? 'Редагування зміни' : 'Нова запланована зміна'}
                  </h4>
                  <div className="restaurant-personnel__form">
                    <label>Початок зміни<input type="datetime-local" required value={shift.startTime} disabled={busy}
                      onChange={event => setShift({ ...shift, startTime: event.target.value })} /></label>
                    <label>Кінець зміни<input type="datetime-local" required value={shift.endTime} disabled={busy}
                      onChange={event => setShift({ ...shift, endTime: event.target.value })} /></label>
                    <label>Примітка графіка<input maxLength={2000} value={shift.notes} disabled={busy}
                      onChange={event => setShift({ ...shift, notes: event.target.value })} /></label>
                    <label><input type="checkbox" checked={shift.isCompleted} disabled={busy}
                      onChange={event => setShift({ ...shift, isCompleted: event.target.checked })} />Зміну виконано</label>
                  </div>
                  <div className="restaurant-personnel__actions">
                    <button disabled={busy}>Зберегти графік</button>
                    <button type="button" disabled={busy} onClick={() => setShift({
                      id: 0, startTime: '', endTime: '', isCompleted: false, notes: '', version: 0,
                    })}>Нова зміна</button>
                  </div>
                </form>}
              </>}
              {dossierView && <RestaurantDossierPanel key={employee.id} employmentId={employee.id}
                canEdit={dossierEdit} canViewSalary={access.permissions.includes('page.management.salary')} />}
            </>}
          </>}
        </section>
      </div>}
      {edit && <section>
        <h2>Внутрішні підрозділи ресторану</h2>
        <form onSubmit={event => { event.preventDefault(); void perform(async () => {
          await api.post('/restaurant-personnel/departments', { name: departmentName },
            { headers: { 'Idempotency-Key': departmentKey.current } });
          departmentKey.current = createIdempotencyKey();
          setDepartmentName(''); await load();
        }); }}>
          <label>Назва нового підрозділу<input required maxLength={200} disabled={busy} value={departmentName}
            onChange={event => setDepartmentName(event.target.value)} /></label>
          <button disabled={busy}>Додати підрозділ</button>
        </form>
      </section>}
      </div>
    </>}
  </main>;
};
const RestaurantPersonnelPage: React.FC = () => {
  const [workspace, setWorkspace] = useState(0);
  useEffect(() => {
    const reset = () => setWorkspace(value => value + 1);
    const storageChanged = (event: StorageEvent) => {
      if (event.key === OPERATING_GROUP_STORAGE_KEY) reset();
    };
    window.addEventListener(OPERATING_GROUP_CHANGED_EVENT, reset);
    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener(OPERATING_GROUP_CHANGED_EVENT, reset);
      window.removeEventListener('storage', storageChanged);
    };
  }, []);
  return <RestaurantPersonnelWorkspace key={workspace} />;
};
export default RestaurantPersonnelPage;
